import Link from '@mui/material/Link';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { pxToRem } from '../../../utils/pxToRem';

export interface RegisterButtonProps {
  label: string;
  href: string;
  ariaLabel: string;
  /** `onDark` = white label (navy hero). `onLight` = gold label (white/grey bands). */
  tone: 'onDark' | 'onLight';
  sx?: SxProps<Theme>;
}

/**
 * Outlined gold CTA (Figma component "Frame 70").
 * External link that opens in a new tab; hover fills gold with a white label.
 */
export function RegisterButton({ label, href, ariaLabel, tone, sx }: RegisterButtonProps) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      underline="none"
      sx={[
        (theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          height: { mobile: pxToRem(40), laptop: pxToRem(52) },
          paddingInline: pxToRem(12),
          paddingBlock: pxToRem(8),
          border: `${pxToRem(2)} solid`,
          borderColor: 'secondary.main',
          borderRadius: pxToRem(8),
          boxShadow: `${pxToRem(2)} ${pxToRem(2)} ${pxToRem(4)} 0 ${alpha(theme.palette.primary.main, 0.2)}`,
          backgroundColor: 'transparent',
          color: tone === 'onDark' ? 'common.white' : 'secondary.main',
          fontFamily: '"Roboto", sans-serif',
          fontWeight: 500,
          fontSize: { mobile: pxToRem(12), laptop: pxToRem(18) },
          lineHeight: { mobile: pxToRem(20), laptop: pxToRem(32) },
          textTransform: 'capitalize',
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
            duration: theme.transitions.duration.short,
          }),
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          '&:hover': {
            backgroundColor: 'secondary.main',
            color: 'common.white',
            boxShadow: `${pxToRem(2)} ${pxToRem(2)} ${pxToRem(2)} 0 ${alpha(theme.palette.primary.main, 0.2)}`,
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label}
    </Link>
  );
}
